// React
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BooleanParam, NumberParam, useQueryParams } from 'use-query-params';

// React & third-party libraries
import { VisibilityState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

// UI components & context
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  Dropdown,
  DropdownType,
  Label,
  LabelType,
  MODAL_TYPES,
  Pill,
  useGlobalModalContext,
} from '../_ui';
import InputSearch from '../_ui/InputSearch/InputSearch.component';

// API hooks
import { useLazyFetchLocationsV3Query } from '../../services/location.api';

// Auth & company hooks
import { USER_ROLE, useAuth } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';

// Types
import { Charger, Location } from '../../stores/types';

// Utilities
import { copyChargingToInuse } from './utils';

// Constants
import { IconSize } from '../../constant/IconSize.constant';
import { NUMBER } from '../../constant/Number.constant';
import { CHARGER_FLAG, CHARGER_STATUS, OCPP_STATUS } from '../../constant/Text.constant';
import { CommaArrayParam, DashArrayParam } from '../../constant/UrlSearchParams.constant';

// Icons & assets
import { ChargerEV, CompanyBuilding, Download, Location as LocationIcon } from '../../assets/icons';

// Charger-related components
import AddCharger from './AddCharger.component';
import { ChargerBulkEditor } from './ChargerBulkEditor';
import { ChargerDataTable } from './ChargerDataTable.component';
import ChargerWrapper from './ChargerWrapper.component';
import { ChargersOverview } from './ChargersOverview.component';
import {
  ChargerColumnsDropdown,
  ColumnConfig,
} from './charger-table/ChargerColumnsDropdown.component';

// Local Storage
import { useLocalStorageState } from '../../hooks/useLocalStorageState';

const CHARGERS_COLUMN_VISIBILITY_STORAGE_KEY = 'chargers.columnVisibility';

export const Chargers = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const { showModal, hideModal } = useGlobalModalContext();
  const { isMultiViewActive, companies, companyScope, companyId } = useCompany();
  const { canCreateChargers, canEditChargers } = useCompanyPermissions(companyId);
  const [isClearInput, setIsClearInput] = useState(false);
  const [chargerListQuery, setChargerListQuery] = useQueryParams({
    status: DashArrayParam,
    locationId: CommaArrayParam,
    maintenanceFlag: BooleanParam,
    searches: CommaArrayParam,
    page: NumberParam,
  });
  const DEFAULT_COLUMN_VISIBILITY = useMemo<VisibilityState>(
    () => ({
      charger: true,
      make: false,
      maxPower: false,
      maxAmperage: false,
      serialNumber: false,
      ocppId: false,
      location: true,
      address: false,
      timezone: false,
      status: true,
      stateOfCharge: false,
      lastUsed: true,
      pricing: false,
      pricingRule: false,
      access: true,
      mapVisibility: false,
      powerManaged: false,
      autoStart: false,
      parkingSpace: false,
      unit: false,
      note: true,
      addedDate: false,
    }),
    [],
  );

  const [columnVisibility, setColumnVisibility] = useLocalStorageState(
    CHARGERS_COLUMN_VISIBILITY_STORAGE_KEY,
    DEFAULT_COLUMN_VISIBILITY,
  );

  const columnConfigs: ColumnConfig[] = useMemo(
    () => [
      {
        id: 'charger',
        label: t('charger'),
        visible: columnVisibility.charger,
        canHide: true,
      },
      {
        id: 'make',
        label: t('charger_make'),
        visible: columnVisibility.make,
        canHide: true,
      },
      {
        id: 'maxPower',
        label: t('charger_max_power'),
        visible: columnVisibility.maxPower,
        canHide: true,
      },
      {
        id: 'maxAmperage',
        label: t('charger_max_amperage'),
        visible: columnVisibility.maxAmperage,
        canHide: true,
      },
      {
        id: 'serialNumber',
        label: t('serial_number'),
        visible: columnVisibility.serialNumber,
        canHide: true,
      },
      {
        id: 'ocppId',
        label: t('chargers_ocpp_id'),
        visible: columnVisibility.ocppId,
        canHide: true,
      },
      {
        id: 'location',
        label: t('location'),
        visible: columnVisibility.location,
        canHide: true,
      },
      {
        id: 'address',
        label: t('charger_address'),
        visible: columnVisibility.address,
        canHide: true,
      },
      {
        id: 'timezone',
        label: t('location_timezone'),
        visible: columnVisibility.timezone,
        canHide: true,
      },
      {
        id: 'status',
        label: t('status'),
        visible: columnVisibility.status,
        canHide: true,
      },
      {
        id: 'stateOfCharge',
        label: t('chargers_grid_stateOfCharge'),
        visible: columnVisibility.stateOfCharge,
        canHide: true,
      },
      {
        id: 'lastUsed',
        label: t('chargers_grid_lastUsed'),
        visible: columnVisibility.lastUsed,
        canHide: true,
      },
      {
        id: 'pricing',
        label: t('pricing'),
        visible: columnVisibility.pricing,
        canHide: true,
      },
      {
        id: 'pricingRule',
        label: t('chargers_pricing_rule'),
        visible: columnVisibility.pricingRule,
        canHide: true,
      },
      {
        id: 'access',
        label: t('access'),
        visible: columnVisibility.access,
        canHide: true,
      },
      {
        id: 'mapVisibility',
        label: t('chargers_map_visibility'),
        visible: columnVisibility.mapVisibility,
        canHide: true,
      },
      {
        id: 'powerManaged',
        label: t('charger_power_managed'),
        visible: columnVisibility.powerManaged,
        canHide: true,
      },
      {
        id: 'autoStart',
        label: t('charger_auto_start'),
        visible: columnVisibility.autoStart,
        canHide: true,
      },
      {
        id: 'parkingSpace',
        label: t('charger_parking_space'),
        visible: columnVisibility.parkingSpace,
        canHide: true,
      },
      {
        id: 'unit',
        label: t('charger_unit'),
        visible: columnVisibility.unit,
        canHide: true,
      },
      {
        id: 'note',
        label: t('chargers_grid_note'),
        visible: columnVisibility.note,
        canHide: true,
      },
      {
        id: 'addedDate',
        label: t('charger_deployment_date_added'),
        visible: columnVisibility.addedDate,
        canHide: true,
      },
    ],
    [columnVisibility, t],
  );
  const handleResetColumnSizingRef = useRef<(() => void) | null>(null);

  const handleColumnVisibilityChange = useCallback((columnId: string, visible: boolean) => {
    setColumnVisibility((prev) => ({ ...prev, [columnId]: visible }));
  }, []);

  const handleResetColumns = useCallback(() => {
    setColumnVisibility(DEFAULT_COLUMN_VISIBILITY);
    handleResetColumnSizingRef.current?.();
  }, [DEFAULT_COLUMN_VISIBILITY]);

  // If any dropdown filter change, reset to page 1
  useEffect(() => {
    const { searches, locationId, status, maintenanceFlag } = chargerListQuery;
    if (searches || locationId || status || maintenanceFlag) {
      setChargerListQuery({ page: 1 });
    }
    // eslint-disable-next-line
  }, [
    chargerListQuery.searches,
    chargerListQuery.locationId,
    chargerListQuery.status,
    chargerListQuery.maintenanceFlag,
    setChargerListQuery,
  ]);
  const handleLoadPage = (page: number) => {
    setChargerListQuery({ page: page });
  };

  // Refetch & get charger id from url (if present) when url query changes
  const selectedStatusFilter = useMemo(() => {
    const ocppStatus = Object.values(OCPP_STATUS);

    return (chargerListQuery?.status || [])?.map((item) => {
      const type = item && ocppStatus.includes(item) ? 'OCPP_STATUS' : 'STATUS';
      return { type, status: item };
    });
  }, [chargerListQuery]);

  const numOfPills = useMemo(() => {
    return (
      (chargerListQuery.searches?.length || 0) +
      (chargerListQuery.locationId?.length || 0) +
      (chargerListQuery.status?.length || 0) +
      (chargerListQuery.maintenanceFlag ? 1 : 0)
    );
  }, [
    chargerListQuery.locationId?.length,
    chargerListQuery.maintenanceFlag,
    chargerListQuery.searches?.length,
    chargerListQuery.status?.length,
  ]);

  const isFilterDisabled = useMemo(() => {
    return numOfPills >= NUMBER.MAX_FILTERS;
  }, [numOfPills]);

  // Refetch & get status from url (if present) when url query changes
  const statusDropdownList = useMemo(() => {
    return Object.values(CHARGER_STATUS)
      .filter(
        (status) =>
          // these 2 status are not in /charger endpoints
          status !== CHARGER_STATUS.PREPARING && status !== CHARGER_STATUS.SCHEDULED,
      )
      .map((status) => {
        if (status === 'in_use') {
          const children = Object.values(OCPP_STATUS).map((ocppStatus) => ({
            status: ocppStatus,
            label: `${ocppStatus}`,
            selected: chargerListQuery.status
              ? chargerListQuery.status?.includes(ocppStatus)
              : false,
            disabled: isFilterDisabled,
          }));

          const isParentDisabled =
            isFilterDisabled ||
            numOfPills + children.filter((child) => !child.selected).length > NUMBER.MAX_FILTERS;

          return {
            status,
            label: `${copyChargingToInuse(status)}`,
            selected: children?.every((child: any) => child.selected) || false,
            children,
            disabled: isParentDisabled,
          };
        }
        return {
          status,
          label: `${copyChargingToInuse(status)}`,
          selected: chargerListQuery.status ? chargerListQuery.status?.includes(status) : false,
          disabled: isFilterDisabled,
        };
      });
  }, [chargerListQuery, isFilterDisabled, numOfPills]);

  const [triggerFetchLocationV3, { data: locationData }] = useLazyFetchLocationsV3Query();

  // Refetch & get maintenanceFlag from url (if present) when url query changes
  const flagDropdownList = useMemo(() => {
    return Object.values(CHARGER_FLAG).map((status) => ({
      status,
      label: `${status}`,
      selected: chargerListQuery.maintenanceFlag || false,
      disabled: isFilterDisabled,
    }));
  }, [chargerListQuery, isFilterDisabled]);

  const [selectedChargers, setSelectedChargers] = useState<Array<Charger>>([]);

  const handleClearAllClick = useCallback(() => {
    setChargerListQuery({
      locationId: undefined,
      status: undefined,
      maintenanceFlag: undefined,
      searches: undefined,
      page: 1,
    });

    // Reset input search values
    setIsClearInput(true);
    setTimeout(() => setIsClearInput(false), NUMBER.ZERO);
  }, [setChargerListQuery, setIsClearInput]);

  useEffect(() => {
    setSelectedChargers([]);
    triggerFetchLocationV3({
      scope: companyScope,
    });
  }, [companyId, companyScope, triggerFetchLocationV3]);

  useEffect(() => {
    const savedCompanyId = localStorage.getItem('companyId');
    if (savedCompanyId && companyId && savedCompanyId !== companyId) {
      handleClearAllClick();
    }
  }, [companyId, handleClearAllClick]);

  const locationDropdownList = useMemo(() => {
    if (!companies || !locationData) {
      return [];
    }
    return companies
      ?.map((company: any) => {
        const children = locationData?.entities
          .filter((item: Location) => item.companyId === company.id)
          .map((location: Location) => {
            return {
              id: location.id,
              label: location.name,
              selected: chargerListQuery.locationId?.some((id) => id === location.id),
              disabled: isFilterDisabled,
            };
          });

        return {
          id: company.name,
          label: company.name,
          companyIcon: CompanyBuilding,
          iconLeftAlt: t('company'),
          iconRightAlt: t('icon_alt_view_companies'),
          selected: children?.every((child: any) => child.selected) ?? false,
          children: children || [],
          disabled: isFilterDisabled,
        };
      })
      .filter((item: any) => !!item.children.length);
  }, [chargerListQuery.locationId, companies, locationData, isFilterDisabled, t]);

  const handleLocationPillClick = (id: any) => {
    setChargerListQuery({
      locationId: chargerListQuery.locationId?.filter((i) => i !== id),
    });
  };

  const handleSearchPillClick = (item: any) => {
    setChargerListQuery({
      searches: chargerListQuery.searches?.filter((i) => i !== item),
    });
  };

  const renderLocationPills = () => {
    const locationPillsToShow: any[] = [];
    locationDropdownList.forEach((ele) => {
      ele.children.forEach((child: any) => {
        if (child.selected) {
          locationPillsToShow.push({
            id: child.id,
            label: child.label,
          });
        }
      });
    });

    return locationPillsToShow.map((item) => {
      return (
        <Pill
          id={item.id}
          iconLeft={LocationIcon}
          key={item.id}
          label={item.label}
          hasCloseButton
          onClick={handleLocationPillClick}
          translationOn
          closeButtonAlt={t('icon_alt_remove_filter_tag')}
        />
      );
    });
  };

  const renderSearchChargerPills = () => {
    return chargerListQuery.searches?.map((item) => {
      return (
        <Pill
          key={item}
          label={`"${item}"`}
          hasCloseButton
          onClick={() => handleSearchPillClick(item)}
          translationOn
          iconLeft={ChargerEV}
          closeButtonAlt={t('icon_alt_remove_filter_tag')}
        />
      );
    });
  };

  const handleStatusPillClick = (clickedItem: any) => {
    if (clickedItem.type === 'OCPP_STATUS') {
      const filterStatus = [...selectedStatusFilter].filter((i) => i.status !== clickedItem.status);
      setChargerListQuery({
        status: filterStatus?.length > 0 ? filterStatus.map((items) => items.status) : undefined,
      });
    } else {
      const filteredStatusList: any = statusDropdownList.find(
        (ele: any) => ele.status === clickedItem.status,
      );

      let filterStatus;
      if (filteredStatusList?.children) {
        const filteredStatusListData: string[] = filteredStatusList?.children.map(
          (ele: any) => ele.status,
        );
        filterStatus = [...selectedStatusFilter].filter((i) => {
          return !filteredStatusListData.includes(i.status || '');
        });
      } else {
        filterStatus = [...selectedStatusFilter].filter((i) => {
          return filteredStatusList.status !== i.status;
        });
      }

      setChargerListQuery({
        status: filterStatus?.length > 0 ? filterStatus.map((item) => item.status) : undefined,
      });
    }
  };

  const handleFlagPillClick = () => {
    setChargerListQuery({
      maintenanceFlag: !chargerListQuery.maintenanceFlag,
    });
  };

  const renderStatusPills = () => {
    const statusPillsToShow: any[] = [];

    statusDropdownList.forEach((ele) => {
      if (ele.selected) {
        statusPillsToShow.push({
          status: ele.status,
          type: 'STATUS',
          label: ele.label,
        });
      } else {
        ele?.children?.forEach((child: any) => {
          if (child.selected) {
            statusPillsToShow.push({
              status: child.status,
              label: child.label,
              type: 'OCPP_STATUS',
            });
          }
        });
      }
    });
    return statusPillsToShow.map((item) => {
      return (
        <Pill
          key={item.status}
          label={item.label}
          hasCloseButton
          onClick={() => handleStatusPillClick(item)}
          translationOn
          closeButtonAlt={t('icon_alt_remove_filter_tag')}
        />
      );
    });
  };

  const renderFlagPills = () => {
    return flagDropdownList
      .filter((item) => item.selected)
      .map((item) => {
        return (
          <Pill
            key={item.status}
            label={item.label}
            hasCloseButton
            onClick={handleFlagPillClick}
            translationOn
            closeButtonAlt={t('icon_alt_remove_filter_tag')}
          />
        );
      });
  };

  const handleEditBulkChargers = () => {
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('edit_chargers'),
      width: '540px',
      height: 'max-content',
      onRenderBody: () => (
        <ChargerBulkEditor
          selectedChargers={selectedChargers}
          setSelectedChargers={setSelectedChargers}
          hideModal={hideModal}
        />
      ),
      shouldCloseOnOverlayClick: false,
    });
  };

  const handleStatusChange = useCallback(
    (items: any[]) => {
      const selectedStatuses: string[] = [];

      items.forEach((item: any) => {
        if (item.children) {
          item.children.forEach((child: any) => {
            if (child.selected) {
              selectedStatuses.push(child.status);
            }
          });
        } else if (item.selected) {
          // Regular status item
          selectedStatuses.push(item.status);
        }
      });

      setChargerListQuery({
        status: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      });
    },
    [setChargerListQuery],
  );

  const handleLocationChange = useCallback(
    (items: any[]) => {
      const selectedLocationIds: string[] = [];

      items.forEach((item: any) => {
        if (item.children) {
          item.children.forEach((child: any) => {
            if (child.selected) {
              selectedLocationIds.push(child.id);
            }
          });
        }
      });

      setChargerListQuery({
        locationId: selectedLocationIds.length > 0 ? selectedLocationIds : undefined,
      });
    },
    [setChargerListQuery],
  );

  const handleUnderMaintenanceDropdownChange = (items: any) => {
    const filteredList = items.filter((item: any) => item.selected === true);
    setChargerListQuery({
      maintenanceFlag: filteredList?.length > 0 ? filteredList[0].selected : undefined,
    });
  };

  const handleSearch = (item: any) => {
    const filteredSearches = chargerListQuery.searches?.filter((search) => search === item) || [];
    let arr: any[];
    if (filteredSearches.length > 0) {
      arr = [...(chargerListQuery.searches || [])];
    } else {
      arr = [...(chargerListQuery.searches || []), item];
    }
    setChargerListQuery({ searches: arr });
  };
  const handleExportRef = useRef<(() => void) | null>(null);

  const renderDropdown = () => {
    return (
      <div className='flex flex-col gap-x-2'>
        {/* Row 1: Edit selected + Add chargers (top right) */}
        {!isMultiViewActive && (
          <div className='flex justify-end gap-3'>
            {canEditChargers && (
              <Button
                onClick={() => {
                  if (selectedChargers.length) {
                    handleEditBulkChargers();
                  }
                }}
                label={`${t('Edit')} ${selectedChargers.length ? selectedChargers.length : ''} ${t(
                  'selected',
                )}`}
                size={ButtonSize.SMALL}
                type={ButtonType.SECONDARY}
                disabled={selectedChargers.length === 0}
                dataTestId='editChargerButton'
              />
            )}
            {canCreateChargers && <AddCharger />}
          </div>
        )}

        {/* Row 2: Filters left, Export right */}
        <div className='flex flex-row justify-between'>
          <div className='flex gap-3 mt-5'>
            {/* Columns dropdown */}
            <ChargerColumnsDropdown
              columns={columnConfigs}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              onReset={handleResetColumns}
            />

            {/* Search input */}
            <InputSearch
              clearInput={isClearInput}
              width='280px'
              iconSize={IconSize.SIZE_24x24}
              inputIconPadding='pl-11'
              suffixIconClassName='mr-4'
              options={[]}
              onSelect={handleSearch}
              placeholder={
                auth.role === USER_ROLE.SUPPORT
                  ? t('charger_or_license_filter_holder')
                  : t('charger_filter_holder')
              }
              contentDivWidth='w-[277px]'
              disabled={isFilterDisabled}
              inputId='charger-input-search'
              inputTitle={
                auth.role === USER_ROLE.SUPPORT
                  ? t('charger_or_license_filter_holder')
                  : t('charger_filter_holder')
              }
              dataTestId='chargerInputSearch'
            />
            <Dropdown
              placeholder={t('location')}
              placeholderLabelType={LabelType.DROPDOWN_HEADER}
              type={DropdownType.CHECKBOX_TREE}
              headerWidth='auto'
              items={locationDropdownList}
              onItemClick={handleLocationChange}
              translationOn
              showFooter
              disabled={isFilterDisabled}
              iconAlt={t('icon_alt_location_dropdown')}
            />
            <Dropdown
              placeholder={t('status')}
              placeholderLabelType={LabelType.DROPDOWN_HEADER}
              type={DropdownType.CHECKBOX_TREE}
              headerWidth='auto'
              items={statusDropdownList}
              onItemClick={handleStatusChange}
              translationOn
              showFooter
              dataTestId='select-status'
              disabled={isFilterDisabled}
              iconAlt={t('icon_alt_status_dropdown')}
            />

            <Dropdown
              placeholder={t('flag')}
              placeholderLabelType={LabelType.DROPDOWN_HEADER}
              type={DropdownType.CHECKBOX}
              headerWidth='auto'
              items={flagDropdownList}
              onItemClick={handleUnderMaintenanceDropdownChange}
              translationOn
              showFooter
              disabled={isFilterDisabled}
              iconAlt={t('icon_alt_maintenance_dropdown')}
            />
          </div>

          {/* Export button on right side of filter row */}
          <div className='flex gap-3 mt-5'>
            <Button
              onClick={() => handleExportRef.current?.()}
              label={t('export_csv')}
              size={ButtonSize.SMALL}
              type={ButtonType.OUTLINE}
              icon={Download}
            />
          </div>
        </div>
      </div>
    );
  };
  const renderClearAllButton = () => {
    if (numOfPills > 0) {
      return (
        <div className='flex flex-row gap-4'>
          <Label
            type={LabelType.BODY3}
            color={ColorType.GREY6}
            text={`${numOfPills}/${NUMBER.TWENTY} ${t('filters')}`}
          />
          <button type='button' className='text-left' onClick={handleClearAllClick}>
            <Label
              type={LabelType.DROPDOWN_ITEM_SELECTED}
              color={ColorType.DENIM}
              text={t('session_clear_all')}
            />
          </button>
        </div>
      );
    }
    return null;
  };

  const renderChargerTable = () => {
    return (
      <div>
        <div className='flex flex-col'>
          {renderDropdown()}
          {numOfPills > 0 && (
            <div className='flex flex-row gap-2 items-baseline justify-between mt-2'>
              <div className='flex flex-row items-center gap-2 my-1.5 flex-wrap'>
                {renderStatusPills()}
                {renderLocationPills()}
                {renderFlagPills()}
                {renderSearchChargerPills()}
              </div>
              <div className='flex flex-row items-baseline min-w-fit'>{renderClearAllButton()}</div>
            </div>
          )}
          <ChargerDataTable
            status={chargerListQuery.status}
            maintenanceFlag={chargerListQuery.maintenanceFlag}
            locationIds={chargerListQuery.locationId}
            searches={chargerListQuery.searches}
            page={chargerListQuery.page || 1}
            handleLoadPage={handleLoadPage}
            selectedChargers={selectedChargers}
            setSelectedChargers={setSelectedChargers}
            columnVisibility={columnVisibility}
            onExportReady={(fn) => {
              handleExportRef.current = fn;
            }}
            onResetColumnSizingReady={(fn) => {
              handleResetColumnSizingRef.current = fn;
            }}
          />
        </div>
      </div>
    );
  };

  const Wrapper = isMultiViewActive ? 'div' : ChargerWrapper;
  return (
    <Wrapper>
      <Card>
        <ChargersOverview />
        {renderChargerTable()}
      </Card>
    </Wrapper>
  );
};
